import { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { FaEdit, FaTrash, FaChevronLeft, FaChevronRight, FaWhatsapp, FaEnvelope, FaPaperPlane, FaCommentDots, FaTimes, FaBookmark, FaSortUp, FaSortDown } from 'react-icons/fa';
import { errorAlert, successAlert } from '../utils/Alerts';
import { useNavigate } from 'react-router-dom';

const ROWS_PER_PAGE_OPTIONS = [5, 10, 20];
const ROLE_OPTIONS = ['Admin', 'Creador', 'Usuario'];

const UserList = ({ token, theme }) => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' = más antiguo primero | 'desc' = más reciente primero

  // ── Selección + mensajería ──
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sendMode, setSendMode] = useState('selected'); // 'selected' | 'role'
  const [targetRole, setTargetRole] = useState('Admin');
  const [channel] = useState('whatsapp'); // 'whatsapp' | 'email'
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const [waResults, setWaResults] = useState(null); // { count, skipped, recipients }
  const [openedLinks, setOpenedLinks] = useState(new Set());

  // ── Plantillas de mensajes ──
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [newTemplateTitle, setNewTemplateTitle] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [newTemplateAsBanner, setNewTemplateAsBanner] = useState(false);

  const navigate = useNavigate();

  const isDark = theme === 'dark';

  useEffect(() => {
    fetchUsers();
  // eslint-disable-next-line
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/message-templates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(data);
    } catch (error) {
      console.error('Error obteniendo plantillas:', error);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    setCurrentPage(1);
    setFilteredUsers(
      users.filter(u =>
        u.username?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.role?.toLowerCase().includes(term) ||
        u.document?.includes(term) ||
        u.celular?.includes(term)
      )
    );
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(prev => prev.filter(u => u._id !== userId));
      setFilteredUsers(prev => prev.filter(u => u._id !== userId));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
      successAlert({ text: 'Usuario eliminado correctamente', timer: 2000 });
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      errorAlert({ title: 'Error', text: 'No se pudo eliminar el usuario' });
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'Admin':   return { bg: 'rgba(99,102,241,0.15)',  color: '#818cf8', border: 'rgba(99,102,241,0.3)' };
      case 'Creador': return { bg: 'rgba(34,197,94,0.15)',   color: '#4ade80', border: 'rgba(34,197,94,0.3)' };
      default:        return { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8', border: 'rgba(148,163,184,0.3)' };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  // ── Orden ──
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const ta = new Date(a.createdAt || 0).getTime();
    const tb = new Date(b.createdAt || 0).getTime();
    return sortOrder === 'asc' ? ta - tb : tb - ta;
  });

  const toggleSort = () => {
    setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    setCurrentPage(1);
  };

  // ── Paginación ──
  const totalPages = Math.ceil(sortedUsers.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedUsers = sortedUsers.slice(startIndex, startIndex + rowsPerPage);

  const handlePrev = () => setCurrentPage(p => Math.max(p - 1, 1));
  const handleNext = () => setCurrentPage(p => Math.min(p + 1, totalPages));

  const handleRowsPerPage = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // ── Selección de usuarios ──
  const toggleSelectUser = (userId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // Selecciona/deselecciona todos los usuarios que coinciden con el filtro actual (todas las páginas)
  const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedIds.has(u._id));

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (allFilteredSelected) {
        // deseleccionar solo los que están visibles en el filtro actual
        const next = new Set(prev);
        filteredUsers.forEach(u => next.delete(u._id));
        return next;
      }
      const next = new Set(prev);
      filteredUsers.forEach(u => next.add(u._id));
      return next;
    });
  };

  // ── Envío de mensajes ──
  const usersByRole = (role) => users.filter(u => u.role === role);

  const recipientCount =
    sendMode === 'selected' ? selectedIds.size : usersByRole(targetRole).length;

  const canSend = messageText.trim().length > 0 && recipientCount > 0 && !sending;

  const handleSendMessage = async () => {
    if (!canSend) return;
    try {
      setSending(true);
      const payload = {
        message: messageText.trim(),
        channel, // por ahora solo 'whatsapp'
        ...(sendMode === 'selected'
          ? { userIds: Array.from(selectedIds) }
          : { role: targetRole }),
      };

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/users/send-message`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setWaResults(data);
      setOpenedLinks(new Set());

      if (data.skipped > 0) {
        successAlert({
          text: `Se generaron ${data.count} links. ${data.skipped} usuario(s) no tienen teléfono registrado.`,
          timer: 3000,
        });
      }
    } catch (error) {
      console.error('Error generando mensaje masivo:', error);
      errorAlert({ title: 'Error', text: error.response?.data?.message || 'No se pudo generar el mensaje' });
    } finally {
      setSending(false);
    }
  };

  const openWhatsappLink = (recipient) => {
    window.open(recipient.whatsappLink, '_blank', 'noopener,noreferrer');
    setOpenedLinks(prev => new Set(prev).add(recipient.userId));
  };

  const closeComposer = () => {
    setShowComposer(false);
    setWaResults(null);
    setMessageText('');
    setSelectedTemplateId('');
    setShowSaveTemplate(false);
    setNewTemplateTitle('');
    setNewTemplateAsBanner(false);
  };

  const openComposer = () => {
    setShowComposer(true);
    fetchTemplates();
  };

  // ── Plantillas ──
  const applyTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const tpl = templates.find(t => t._id === templateId);
    if (tpl) setMessageText(tpl.content);
  };

  const handleSaveTemplate = async () => {
    if (!newTemplateTitle.trim() || !messageText.trim()) return;
    try {
      setSavingTemplate(true);
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/message-templates`,
        {
          title: newTemplateTitle.trim(),
          content: messageText.trim(),
          type: newTemplateAsBanner ? 'banner' : 'template',
          isActive: newTemplateAsBanner,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(prev => [data, ...prev]);
      setSelectedTemplateId(data._id);
      setShowSaveTemplate(false);
      setNewTemplateTitle('');
      setNewTemplateAsBanner(false);
      successAlert({
        text: newTemplateAsBanner
          ? 'Plantilla guardada y publicada como banner'
          : 'Plantilla guardada correctamente',
        timer: 2000,
      });
    } catch (error) {
      console.error('Error guardando plantilla:', error);
      errorAlert({ title: 'Error', text: 'No se pudo guardar la plantilla' });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleToggleBannerActive = async (template) => {
    try {
      const { data } = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/message-templates/${template._id}`,
        { isActive: !template.isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(prev => prev.map(t => t._id === data._id ? data : t));
      successAlert({
        text: data.isActive ? 'Banner activado' : 'Banner desactivado',
        timer: 1500,
      });
    } catch (error) {
      console.error('Error actualizando banner:', error);
      errorAlert({ title: 'Error', text: 'No se pudo actualizar el banner' });
    }
  };

  const handleDeleteTemplate = async (templateId) => {
  if (!templateId) return;
    if (!window.confirm('¿Eliminar esta plantilla guardada?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/message-templates/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(prev => prev.filter(t => t._id !== templateId));
      if (selectedTemplateId === templateId) {
        setSelectedTemplateId('');
      }
      successAlert({ text: 'Plantilla eliminada', timer: 1500 });
    } catch (error) {
      console.error('Error eliminando plantilla:', error);
      errorAlert({ title: 'Error', text: 'No se pudo eliminar la plantilla' });
    }
  };

  return (
    <MainContainer active={isDark}>
      <TopBar>
        <Title active={isDark}>Usuarios</Title>
        <UserCount active={isDark}>{filteredUsers.length} usuarios</UserCount>
      </TopBar>

      {/* ── Modal: Compositor de mensajes masivos ── */}
      {showComposer && (
        <ModalOverlay onClick={closeComposer}>
          <ModalCard active={isDark} onClick={(e) => e.stopPropagation()}>
            <ModalHeader active={isDark}>
              <ModalTitle active={isDark}>
                <FaCommentDots /> Mensaje masivo
              </ModalTitle>
              <CloseButton active={isDark} onClick={closeComposer}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>

            {!waResults ? (
              <>
                <ModalBody>
                  <ComposerRow>
                    <ModeToggle>
                      <ModeButton
                        active={isDark}
                        selected={sendMode === 'selected'}
                        onClick={() => setSendMode('selected')}
                      >
                        Seleccionados ({selectedIds.size})
                      </ModeButton>
                      <ModeButton
                        active={isDark}
                        selected={sendMode === 'role'}
                        onClick={() => setSendMode('role')}
                      >
                        Por rol
                      </ModeButton>
                    </ModeToggle>

                    {sendMode === 'role' && (
                      <RoleSelect
                        active={isDark}
                        value={targetRole}
                        onChange={(e) => setTargetRole(e.target.value)}
                      >
                        {ROLE_OPTIONS.map(r => (
                          <option key={r} value={r}>{r} ({usersByRole(r).length})</option>
                        ))}
                      </RoleSelect>
                    )}
                  </ComposerRow>

                  <ComposerRow>
                    <ChannelToggle full>
                      <ChannelButton active={isDark} selected title="WhatsApp">
                        <FaWhatsapp /> WhatsApp
                      </ChannelButton>
                      <ChannelButton active={isDark} selected={false} disabled title="Próximamente">
                        <FaEnvelope /> Correo (próximamente)
                      </ChannelButton>
                    </ChannelToggle>
                  </ComposerRow>

                  {/* ── Selector de plantillas ── */}
                  <ComposerRow>
  <TemplateSelect
    active={isDark}
    value={selectedTemplateId}
    onChange={(e) => applyTemplate(e.target.value)}
  >
    <option value="">— Usar plantilla guardada —</option>
    {templates.map(t => (
      <option key={t._id} value={t._id}>
        {t.type === 'banner' ? '📣 ' : ''}{t.title}{t.type === 'banner' && t.isActive ? ' (activo)' : ''}
      </option>
    ))}
  </TemplateSelect>

  {selectedTemplateId && (
    <DeleteTemplateButton
      active={isDark}
      type="button"
      onClick={() => handleDeleteTemplate(selectedTemplateId)}
      title="Eliminar esta plantilla"
    >
      <FaTrash />
    </DeleteTemplateButton>
  )}

  <SaveTemplateButton
    active={isDark}
    type="button"
    onClick={() => setShowSaveTemplate(v => !v)}
    disabled={!messageText.trim()}
    title="Guardar mensaje actual como plantilla"
  >
    <FaBookmark /> Guardar
  </SaveTemplateButton>
                    </ComposerRow>

                    {/* ── Toggle de banner si la plantilla seleccionada ya es un banner ── */}
                    {selectedTemplateId && (() => {
                      const selectedTpl = templates.find(t => t._id === selectedTemplateId);
                      if (!selectedTpl || selectedTpl.type !== 'banner') return null;
                      return (
                        <ComposerRow>
                          <BannerStatusBadge isActive={selectedTpl.isActive} active={isDark}>
                            📣 {selectedTpl.isActive ? 'Banner activo en el home' : 'Banner inactivo'}
                          </BannerStatusBadge>
                          <ToggleBannerButton
                            active={isDark}
                            isActive={selectedTpl.isActive}
                            type="button"
                            onClick={() => handleToggleBannerActive(selectedTpl)}
                          >
                            {selectedTpl.isActive ? 'Desactivar' : 'Activar'}
                          </ToggleBannerButton>
                        </ComposerRow>
                      );
                    })()}

                    {showSaveTemplate && (
                      <>
                        <ComposerRow>
                          <TemplateNameInput
                            active={isDark}
                            placeholder="Nombre de la plantilla (ej: Oferta 80%)"
                            value={newTemplateTitle}
                            onChange={(e) => setNewTemplateTitle(e.target.value)}
                            autoFocus
                          />
                          <SendButton
                            disabled={!newTemplateTitle.trim() || savingTemplate}
                            onClick={handleSaveTemplate}
                          >
                            {savingTemplate ? 'Guardando...' : 'Confirmar'}
                          </SendButton>
                        </ComposerRow>

                        <BannerCheckboxRow active={isDark}>
                          <input
                            type="checkbox"
                            id="asBanner"
                            checked={newTemplateAsBanner}
                            onChange={(e) => setNewTemplateAsBanner(e.target.checked)}
                          />
                          <label htmlFor="asBanner">
                            📣 Usar también como banner publicitario (se mostrará activo en el home)
                          </label>
                        </BannerCheckboxRow>
                      </>
                    )}

                  <ComposerRow>
                    <MessageTextarea
                      active={isDark}
                      placeholder="Escribe el mensaje que deseas enviar..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      rows={5}
                      autoFocus
                    />
                  </ComposerRow>
                </ModalBody>

                <ModalFooter active={isDark}>
                  <RecipientInfo active={isDark}>
                    {recipientCount > 0
                      ? `Se generarán links para ${recipientCount} usuario${recipientCount === 1 ? '' : 's'}`
                      : 'No hay destinatarios seleccionados'}
                  </RecipientInfo>
                  <SendButton disabled={!canSend} onClick={handleSendMessage}>
                    <FaPaperPlane /> {sending ? 'Generando...' : 'Generar links'}
                  </SendButton>
                </ModalFooter>
              </>
            ) : (
              <>
                <ModalBody>
                  <RecipientInfo active={isDark} style={{ display: 'block', marginBottom: 4 }}>
                    {waResults.count} link{waResults.count === 1 ? '' : 's'} listo{waResults.count === 1 ? '' : 's'} para enviar
                    {waResults.skipped > 0 && ` · ${waResults.skipped} usuario(s) sin teléfono registrado`}
                  </RecipientInfo>
                  <ResultsList>
                    {waResults.recipients.map((r) => (
                      <ResultRow key={r.userId} active={isDark} opened={openedLinks.has(r.userId)}>
                        <ResultName active={isDark}>{r.username}</ResultName>
                        <ResultPhone active={isDark}>{r.celular}</ResultPhone>
                        <OpenLinkButton onClick={() => openWhatsappLink(r)}>
                          <FaWhatsapp /> {openedLinks.has(r.userId) ? 'Abierto ✓' : 'Abrir'}
                        </OpenLinkButton>
                      </ResultRow>
                    ))}
                  </ResultsList>
                </ModalBody>

                <ModalFooter active={isDark}>
                  <RecipientInfo active={isDark}>
                    {openedLinks.size} de {waResults.count} abiertos
                  </RecipientInfo>
                  <SendButton onClick={closeComposer}>
                    Listo
                  </SendButton>
                </ModalFooter>
              </>
            )}
          </ModalCard>
        </ModalOverlay>
      )}

      <SearchBarRow>
        <SearchWrapper>
          <SearchIcon>🔍</SearchIcon>
          <SearchInput
            active={isDark}
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Buscar por nombre, email, rol, cédula..."
          />
        </SearchWrapper>

        <MassMessageButton onClick={openComposer}>
          <FaCommentDots /> Mensaje masivo
          {selectedIds.size > 0 && <BadgeCount>{selectedIds.size}</BadgeCount>}
        </MassMessageButton>
      </SearchBarRow>

      <TableCard active={isDark}>
        {loading ? (
          <EmptyMessage active={isDark}>Cargando usuarios...</EmptyMessage>
        ) : filteredUsers.length === 0 ? (
          <EmptyMessage active={isDark}>No se encontraron usuarios.</EmptyMessage>
        ) : (
          <>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th active={isDark} center style={{ width: 40 }}>
                      <Checkbox
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={toggleSelectAll}
                      />
                    </Th>
                    <Th active={isDark}>Usuario</Th>
                    <Th active={isDark}>Cédula</Th>
                    <Th active={isDark}>Teléfono</Th>
                    <Th active={isDark}>Rol</Th>
                    <Th active={isDark}>
                      <SortButton
                        type="button"
                        active={isDark}
                        onClick={toggleSort}
                        title={sortOrder === 'asc' ? 'Más antiguos primero' : 'Más recientes primero'}
                      >
                        Fecha Registro
                        {sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />}
                      </SortButton>
                    </Th>
                    <Th active={isDark} center>Acciones</Th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((user) => {
                    const badge = getRoleBadge(user.role);
                    return (
                      <Tr key={user._id} active={isDark} selected={selectedIds.has(user._id)}>
                        <Td active={isDark} center>
                          <Checkbox
                            type="checkbox"
                            checked={selectedIds.has(user._id)}
                            onChange={() => toggleSelectUser(user._id)}
                          />
                        </Td>
                        <Td active={isDark}>
                          <UserInfo>
                            <Avatar active={isDark}>
                              {user.username?.charAt(0).toUpperCase()}
                            </Avatar>
                            <UserDetails>
                              <UserNameText active={isDark}>{user.username}</UserNameText>
                              <UserEmail active={isDark}>{user.email}</UserEmail>
                            </UserDetails>
                          </UserInfo>
                        </Td>
                        <Td active={isDark}>{user.document || '—'}</Td>
                        <Td active={isDark}>{user.celular || '—'}</Td>
                        <Td active={isDark}>
                          <RoleBadge bg={badge.bg} color={badge.color} border={badge.border}>
                            {user.role}
                          </RoleBadge>
                        </Td>
                        <Td active={isDark}>{formatDate(user.createdAt)}</Td>
                        <Td active={isDark} center>
                          <ActionButtons>
                            <EditButton active={isDark} title="Editar" onClick={() => navigate(`/dashboard/users/${user._id}`)}>
                                <FaEdit />
                            </EditButton>
                            <DeleteButton active={isDark} title="Eliminar" onClick={() => handleDelete(user._id)}>
                              <FaTrash />
                            </DeleteButton>
                          </ActionButtons>
                        </Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>

            {/* ── Paginación ── */}
            <PaginationBar active={isDark}>
              <RowsSelector>
                <RowsLabel active={isDark}>Filas por página:</RowsLabel>
                <RowsSelect active={isDark} value={rowsPerPage} onChange={handleRowsPerPage}>
                  {ROWS_PER_PAGE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </RowsSelect>
              </RowsSelector>

              <PageInfo active={isDark}>
                {startIndex + 1}–{Math.min(startIndex + rowsPerPage, sortedUsers.length)} de {sortedUsers.length}
              </PageInfo>

              <PageButtons>
                <PageBtn active={isDark} onClick={handlePrev} disabled={currentPage === 1}>
                  <FaChevronLeft />
                </PageBtn>
                <PageBtn active={isDark} onClick={handleNext} disabled={currentPage === totalPages}>
                  <FaChevronRight />
                </PageBtn>
              </PageButtons>
            </PaginationBar>
          </>
        )}
      </TableCard>
    </MainContainer>
  );
};

/* ─── Styled Components ─── */

const MainContainer = styled.div`
  background-color: ${p => p.active ? '#000000' : '#480e0e'};
  height: 100%;
  padding: 40px;
  transition: background-color 0.3s ease;
  @media (max-width: 768px) { padding: 20px; }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${p => p.active ? '#ffffff' : '#222'};
  margin: 0;
`;

const UserCount = styled.span`
  font-size: 13px;
  color: ${p => p.active ? '#666' : '#999'};
`;

const MassMessageButton = styled.button`
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  background: #6366f1;
  color: #fff;
  transition: all 0.2s ease;
  &:hover { background: #5457e5; }
`;

const BadgeCount = styled.span`
  background: #fff;
  color: #6366f1;
  font-size: 11px;
  font-weight: 800;
  border-radius: 999px;
  padding: 1px 7px;
  line-height: 1.4;
`;

/* ── Modal ── */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalCard = styled.div`
  background-color: ${p => p.active ? '#1e1e1e' : '#ffffff'};
  border: 1px solid ${p => p.active ? '#2a2a2a' : '#e0e0e0'};
  border-radius: 14px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  animation: modalPop 0.18s ease;

  @keyframes modalPop {
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid ${p => p.active ? '#2a2a2a' : '#f0f0f0'};
`;

const ModalTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${p => p.active ? '#fff' : '#222'};
`;

const CloseButton = styled.button`
  background: ${p => p.active ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)'};
  border: 1px solid ${p => p.active ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
  color: ${p => p.active ? '#ccc' : '#555'};
  width: 28px;
  height: 28px;
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  &:hover {
    background: ${p => p.active ? 'rgba(220,53,69,0.25)' : 'rgba(220,53,69,0.1)'};
    border-color: #dc3545;
    color: #dc3545;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid ${p => p.active ? '#2a2a2a' : '#f0f0f0'};
`;

const ComposerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: ${p => p.between ? 'space-between' : 'flex-start'};
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${p => p.opened
    ? (p.active ? 'rgba(37,211,102,0.1)' : 'rgba(37,211,102,0.08)')
    : (p.active ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)')};
  border: 1px solid ${p => p.opened ? 'rgba(37,211,102,0.35)' : (p.active ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')};
`;

const ResultName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.active ? '#fff' : '#222'};
  flex: 1;
`;

const ResultPhone = styled.span`
  font-size: 12px;
  color: ${p => p.active ? '#888' : '#777'};
`;

const OpenLinkButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid #25D366;
  background: rgba(37,211,102,0.12);
  color: #25D366;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  &:hover { background: rgba(37,211,102,0.22); }
`;

const ModeToggle = styled.div`
  display: flex;
  gap: 6px;
`;

const ModeButton = styled.button`
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${p => p.selected ? '#6366f1' : (p.active ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)')};
  background: ${p => p.selected ? 'rgba(99,102,241,0.15)' : (p.active ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)')};
  color: ${p => p.selected ? '#818cf8' : (p.active ? '#ccc' : '#555')};
  transition: all 0.2s ease;
`;

const RoleSelect = styled.select`
  background-color: ${p => p.active ? '#2a2a2a' : '#f5f5f5'};
  border: 1px solid ${p => p.active ? '#444' : '#ddd'};
  border-radius: 8px;
  color: ${p => p.active ? '#ccc' : '#555'};
  padding: 7px 10px;
  font-size: 13px;
  outline: none;
  cursor: pointer;
`;

const ChannelToggle = styled.div`
  display: flex;
  gap: 6px;
  width: ${p => p.full ? '100%' : 'auto'};
  margin-left: ${p => p.full ? '0' : 'auto'};
`;

const ChannelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex: 1;
  border: 1px solid ${p => p.selected ? '#25D366' : (p.active ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)')};
  background: ${p => p.selected ? 'rgba(37,211,102,0.15)' : (p.active ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)')};
  color: ${p => p.selected ? '#25D366' : (p.active ? '#ccc' : '#555')};
  transition: all 0.2s ease;
  opacity: ${p => p.disabled ? 0.45 : 1};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
`;

const MessageTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  background-color: ${p => p.active ? '#1a1a1a' : '#fafafa'};
  border: 1px solid ${p => p.active ? '#333' : '#ddd'};
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  color: ${p => p.active ? '#e0e0e0' : '#333'};
  outline: none;
  resize: vertical;
  font-family: inherit;
  &::placeholder { color: ${p => p.active ? '#555' : '#aaa'}; }
  &:focus { border-color: rgba(128,128,128,0.6); }
`;

const RecipientInfo = styled.span`
  font-size: 13px;
  color: ${p => p.active ? '#888' : '#777'};
`;

const SendButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  background: #6366f1;
  color: #fff;
  transition: all 0.2s ease;
  &:hover:not(:disabled) { background: #5457e5; }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/* ── Plantillas ── */
const TemplateSelect = styled.select`
  flex: 1;
  min-width: 200px;
  background-color: ${p => p.active ? '#2a2a2a' : '#f5f5f5'};
  border: 1px solid ${p => p.active ? '#444' : '#ddd'};
  border-radius: 8px;
  color: ${p => p.active ? '#ccc' : '#555'};
  padding: 8px 10px;
  font-size: 13px;
  outline: none;
  cursor: pointer;
`;

const SaveTemplateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${p => p.active ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
  background: ${p => p.active ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  color: ${p => p.active ? '#ccc' : '#555'};
  transition: all 0.2s ease;
  opacity: ${p => p.disabled ? 0.4 : 1};
  &:hover:not(:disabled) { background: ${p => p.active ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}; }
`;

const TemplateNameInput = styled.input`
  flex: 1;
  box-sizing: border-box;
  background-color: ${p => p.active ? '#1a1a1a' : '#fafafa'};
  border: 1px solid ${p => p.active ? '#333' : '#ddd'};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: ${p => p.active ? '#e0e0e0' : '#333'};
  outline: none;
  &::placeholder { color: ${p => p.active ? '#555' : '#aaa'}; }
`;

const SearchBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const SearchWrapper = styled.div`
  position: relative;
  max-width: 420px;
  width: 100%;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background-color: ${p => p.active ? '#1e1e1e' : '#ffffff'};
  border: 1px solid ${p => p.active ? '#333' : '#ddd'};
  border-radius: 8px;
  padding: 10px 14px 10px 38px;
  font-size: 14px;
  color: ${p => p.active ? '#e0e0e0' : '#333'};
  outline: none;
  transition: all 0.3s ease;
  &::placeholder { color: ${p => p.active ? '#555' : '#aaa'}; }
  &:focus { border-color: rgba(128,128,128,0.6); }
`;

const TableCard = styled.div`
  background-color: ${p => p.active ? '#1e1e1e' : '#ffffff'};
  border: 1px solid ${p => p.active ? '#2a2a2a' : '#e0e0e0'};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px ${p => p.active ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.06)'};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: ${p => p.center ? 'center' : 'left'};
  padding: 13px 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${p => p.active ? '#666' : '#999'};
  border-bottom: 1px solid ${p => p.active ? '#2a2a2a' : '#f0f0f0'};
  background-color: ${p => p.active ? '#1a1a1a' : '#fafafa'};
  white-space: nowrap;
`;

const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  color: ${p => p.active ? '#818cf8' : '#6366f1'};
  &:hover { opacity: 0.8; }
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${p => p.active ? '#2a2a2a' : '#f5f5f5'};
  transition: background-color 0.15s ease;
  background-color: ${p => p.selected ? (p.active ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.05)') : 'transparent'};
  &:last-child { border-bottom: none; }
  &:hover { background-color: ${p => p.active ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}; }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 13px;
  color: ${p => p.active ? '#ccc' : '#555'};
  vertical-align: middle;
  white-space: nowrap;
  text-align: ${p => p.center ? 'center' : 'left'};
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #6366f1;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background-color: ${p => p.active ? '#333' : '#e0e0e0'};
  color: ${p => p.active ? '#fff' : '#555'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserNameText = styled.span`
  font-weight: 600;
  color: ${p => p.active ? '#fff' : '#222'};
  font-size: 13px;
`;

const UserEmail = styled.span`
  font-size: 11px;
  color: ${p => p.active ? '#555' : '#aaa'};
  margin-top: 2px;
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${p => p.bg};
  color: ${p => p.color};
  border: 1px solid ${p => p.border};
  white-space: nowrap;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 6px;
  justify-content: center;
`;

const EditButton = styled.button`
  background: ${p => p.active ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)'};
  border: 1px solid ${p => p.active ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
  color: ${p => p.active ? '#ccc' : '#555'};
  width: 30px;
  height: 30px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  &:hover {
    background: ${p => p.active ? 'rgba(255,193,7,0.2)' : 'rgba(255,193,7,0.12)'};
    border-color: #ffc107;
    color: #ffc107;
    transform: scale(1.1);
  }
`;

const DeleteButton = styled.button`
  background: ${p => p.active ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)'};
  border: 1px solid ${p => p.active ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
  color: ${p => p.active ? '#ccc' : '#555'};
  width: 30px;
  height: 30px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  &:hover {
    background: ${p => p.active ? 'rgba(220,53,69,0.25)' : 'rgba(220,53,69,0.1)'};
    border-color: #dc3545;
    color: #dc3545;
    transform: scale(1.1);
  }
`;

const EmptyMessage = styled.p`
  text-align: center;
  padding: 40px;
  color: ${p => p.active ? '#555' : '#aaa'};
  font-size: 15px;
`;

/* ── Paginación ── */
const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  padding: 12px 16px;
  border-top: 1px solid ${p => p.active ? '#2a2a2a' : '#f0f0f0'};
`;

const RowsSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RowsLabel = styled.span`
  font-size: 13px;
  color: ${p => p.active ? '#666' : '#999'};
`;

const RowsSelect = styled.select`
  background-color: ${p => p.active ? '#2a2a2a' : '#f5f5f5'};
  border: 1px solid ${p => p.active ? '#444' : '#ddd'};
  border-radius: 6px;
  color: ${p => p.active ? '#ccc' : '#555'};
  padding: 4px 8px;
  font-size: 13px;
  outline: none;
  cursor: pointer;
`;

const PageInfo = styled.span`
  font-size: 13px;
  color: ${p => p.active ? '#666' : '#999'};
`;

const PageButtons = styled.div`
  display: flex;
  gap: 4px;
`;

const PageBtn = styled.button`
  background: ${p => p.active ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)'};
  border: 1px solid ${p => p.active ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
  color: ${p => p.active ? '#ccc' : '#555'};
  width: 30px;
  height: 30px;
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${p => p.active ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
  }
`;

const DeleteTemplateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${p => p.active ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
  background: ${p => p.active ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  color: ${p => p.active ? '#ccc' : '#555'};
  transition: all 0.2s ease;
  &:hover {
    background: ${p => p.active ? 'rgba(220,53,69,0.25)' : 'rgba(220,53,69,0.1)'};
    border-color: #dc3545;
    color: #dc3545;
  }
`;

const BannerCheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${p => p.active ? '#aaa' : '#666'};
  cursor: pointer;
  input { cursor: pointer; accent-color: #a855f7; }
`;

const BannerStatusBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 20px;
  color: ${p => p.isActive ? '#22c55e' : (p.active ? '#888' : '#999')};
  background: ${p => p.isActive ? 'rgba(34,197,94,0.12)' : (p.active ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)')};
  border: 1px solid ${p => p.isActive ? 'rgba(34,197,94,0.3)' : (p.active ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)')};
`;

const ToggleBannerButton = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid ${p => p.isActive ? '#dc3545' : '#22c55e'};
  background: ${p => p.isActive ? 'rgba(220,53,69,0.1)' : 'rgba(34,197,94,0.1)'};
  color: ${p => p.isActive ? '#dc3545' : '#22c55e'};
  transition: all 0.2s ease;
  &:hover { background: ${p => p.isActive ? 'rgba(220,53,69,0.2)' : 'rgba(34,197,94,0.2)'}; }
`;

export default UserList;